import { useState, type ChangeEvent, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import {
  supplierDataApi,
  type SupplierDataMethod,
  type SupplierDataSource,
  type SupplierApiTestResult,
  type SheetVerification,
  type MappingProposal,
  type MediaCheckResult,
  type ConnectionSpec,
} from '../../api/endpoints'
import { ApiError } from '../../api/types'

/**
 * "How does your stock reach us?" — the ONE supplier data-onboarding surface,
 * rendered by the signup wizard's supplier step, by the My Stock "connect your
 * inventory" card, and therefore by BOTH shells.
 *
 * ── REBUILT 2026-08-19, because the old one could not work ─────────────────
 * The API door collected {url, key, header} and tested it with a plain GET.
 * All three feeds we actually ingest are POST (Nexot: JSON body; AVP: JSON body
 * + query token; Navrang: multipart + lowercase `apikey` header). Not one real
 * supplier could have passed Test connection. Worse, nothing in
 * `functions/src/integrations/` ever read what was saved — so the green tick was
 * over a config that would never deliver a stone.
 *
 * Three things changed:
 *   1. THE PRIMARY DOOR IS "send us your docs". Tej: "most admins may not even
 *      understand how to use the api input logic". A PDF and a link is a thing
 *      every supplier has; a request-shape form is not.
 *   2. THE API DOOR CAN EXPRESS A REAL FEED — method, body, and where the key
 *      goes. It is the second door, not the first.
 *   3. NOTHING SAYS "CONNECTED" UNTIL IT IS. The states are honest all the way
 *      down (see STATUS_COPY, served by the API), and a mapping is previewed
 *      against the supplier's OWN values and explicitly confirmed before it can
 *      price anything.
 *
 * Controlled: the parent owns the draft (the wizard keeps it OUT of its
 * persisted sessionStorage state — the API key must never touch storage) and
 * performs the save. Test / verify / upload actions run in here and write
 * their results back into the draft.
 */

export interface DataSourceDraft {
  method: SupplierDataMethod | null
  /** ── docs door */
  docsUrl: string
  docsFile: { objectPath: string; fileName: string } | null
  /** ── api door */
  apiUrl: string
  apiKey: string
  httpMethod: 'GET' | 'POST'
  bodyMode: 'none' | 'json' | 'form'
  bodyJson: string
  formFields: Array<{ name: string; value: string }>
  authMode: 'none' | 'bearer' | 'header' | 'query'
  apiKeyHeader: string
  apiKeyParam: string
  /** True when a key is already stored server-side (editing an existing config). */
  hasSavedKey: boolean
  /** ── excel door */
  sheet: { objectPath: string; fileName: string } | null
  /** ── shared */
  notes: string
  answers: Record<string, string>
  mediaPattern: string
  mappingConfirmed: boolean
  lastTest: SupplierApiTestResult | null
  sheetVerification: SheetVerification | null
  mediaCheck: MediaCheckResult | null
}

export const EMPTY_DRAFT: DataSourceDraft = {
  method: null,
  docsUrl: '',
  docsFile: null,
  apiUrl: '',
  apiKey: '',
  // POST by default: all three feeds we actually ingest are POST, and a GET
  // default is why not one real supplier could pass Test connection (see the
  // module header). A supplier who genuinely has a GET feed switches it.
  httpMethod: 'POST',
  bodyMode: 'json',
  bodyJson: '{}',
  formFields: [],
  authMode: 'none',
  apiKeyHeader: '',
  apiKeyParam: '',
  hasSavedKey: false,
  sheet: null,
  notes: '',
  answers: {},
  mediaPattern: '',
  mappingConfirmed: false,
  lastTest: null,
  sheetVerification: null,
  mediaCheck: null,
}

/** Seed a draft from a saved config (My Stock edit; key stays server-side). */
export function draftFromSaved(ds: SupplierDataSource | null): DataSourceDraft {
  if (!ds) return EMPTY_DRAFT
  const c = ds.connection
  return {
    ...EMPTY_DRAFT,
    // `erp_push` is a LEGACY value: its door (docs link + instruction upload) is
    // exactly what the docs door now is, so an old record opens in the new one
    // rather than on a screen that no longer exists.
    method: ds.method === 'later' ? null : ds.method === 'erp_push' ? 'docs' : ds.method,
    docsUrl: ds.docs?.url ?? ds.erp?.docsUrl ?? '',
    docsFile: ds.docs?.file ?? ds.erp?.instructions ?? null,
    apiUrl: c?.url ?? ds.api?.url ?? '',
    apiKey: '',
    httpMethod: c?.httpMethod ?? 'GET',
    bodyMode: c?.bodyMode ?? 'none',
    bodyJson: c?.bodyJson ?? '{}',
    formFields: c?.formFields ?? [],
    authMode: c?.authMode ?? (ds.api?.keyHeader ? 'header' : ds.api?.hasKey ? 'bearer' : 'none'),
    apiKeyHeader: c?.apiKeyHeader ?? ds.api?.keyHeader ?? '',
    apiKeyParam: c?.apiKeyParam ?? '',
    hasSavedKey: !!ds.api?.hasKey,
    sheet: ds.sheet ? { objectPath: ds.sheet.objectPath, fileName: ds.sheet.fileName } : null,
    notes: ds.mapping?.notes ?? ds.notes ?? '',
    answers: ds.mapping?.answers ?? {},
    mediaPattern: ds.mapping?.mediaPattern ?? '',
    mappingConfirmed: ds.mapping?.confirmed ?? false,
    lastTest: ds.lastTest ?? null,
    sheetVerification: ds.sheetVerification ?? null,
    mediaCheck: ds.mediaCheck ?? null,
  }
}

/** The connection spec a draft describes (no credential — see supplierMapping). */
export function draftToConnection(draft: DataSourceDraft): ConnectionSpec {
  return {
    url: draft.apiUrl.trim(),
    httpMethod: draft.httpMethod,
    bodyMode: draft.httpMethod === 'POST' ? draft.bodyMode : 'none',
    ...(draft.httpMethod === 'POST' && draft.bodyMode === 'json' ? { bodyJson: draft.bodyJson } : {}),
    ...(draft.httpMethod === 'POST' && draft.bodyMode === 'form'
      ? { formFields: draft.formFields.filter((f) => f.name.trim()) }
      : {}),
    authMode: draft.authMode,
    ...(draft.authMode === 'header' && draft.apiKeyHeader.trim() ? { apiKeyHeader: draft.apiKeyHeader.trim() } : {}),
    ...(draft.authMode === 'query' && draft.apiKeyParam.trim() ? { apiKeyParam: draft.apiKeyParam.trim() } : {}),
  }
}

/** The POST /me/supplier-data-source body for a draft (parent calls save). */
export function draftToSaveBody(draft: DataSourceDraft) {
  const method = draft.method ?? 'later'
  return {
    method,
    ...(method === 'docs' && draft.docsUrl.trim() ? { docsUrl: draft.docsUrl.trim() } : {}),
    ...(method === 'docs' && draft.docsFile ? { docsFile: draft.docsFile } : {}),
    // The docs door takes an endpoint too when they happen to know it — the one
    // fact that shortens our integration is not worth throwing away.
    ...(draft.apiUrl.trim() && (method === 'docs' || method === 'api') ? { apiUrl: draft.apiUrl.trim() } : {}),
    ...(draft.apiKey.trim() && (method === 'docs' || method === 'api') ? { apiKey: draft.apiKey.trim() } : {}),
    ...(method === 'api' ? { connection: draftToConnection(draft) } : {}),
    ...(method === 'excel' && draft.sheet ? { sheet: draft.sheet } : {}),
    ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
  }
}

/** Client-side completeness check — what "Continue" should require per method. */
export function draftIsSubmittable(draft: DataSourceDraft): { ok: boolean; reason?: string } {
  if (draft.method === 'docs' && !draft.docsUrl.trim() && !draft.docsFile) {
    return { ok: false, reason: 'Add a link to your API docs or upload the file — or choose "I’ll do this later".' }
  }
  if (draft.method === 'api') {
    if (!draft.apiUrl.trim()) {
      return { ok: false, reason: 'Add your feed URL — or choose "I’ll do this later".' }
    }
    // A URL alone is not a working feed — it's the shape of the old lie. A
    // supplier saying "api" is committing to a connection we could actually
    // read, so require a green Test connection before we treat it as such.
    // The escape hatch is honest: choose docs, or "I'll do this later".
    if (!draft.lastTest?.ok) {
      return { ok: false, reason: 'Test your connection first, or choose "I’ll do this later".' }
    }
  }
  if (draft.method === 'excel' && !draft.sheet) {
    return { ok: false, reason: 'Upload a sample sheet — or choose "I’ll do this later".' }
  }
  return { ok: true }
}

/** The proposal to review, whichever door produced it. */
export function proposalOf(draft: DataSourceDraft): MappingProposal | null {
  return draft.lastTest?.mapping ?? draft.sheetVerification?.mapping ?? null
}

interface MethodCard {
  key: SupplierDataMethod
  title: string
  blurb: string
  badge?: string
  icon: ReactNode
}

/* Per-card iconography (inline SVG — no asset request; inherits currentColor so
 * the tone follows the card state). docs = document, api = code brackets,
 * excel = spreadsheet grid. */
const ICON_DOCS = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h8" /><path d="M8 9h2" />
  </svg>
)
const ICON_API = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m8 6-6 6 6 6" /><path d="m16 6 6 6-6 6" /><path d="m13 4-2 16" />
  </svg>
)
const ICON_EXCEL = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M3 15h18" /><path d="M9 4v16" /><path d="M15 4v16" />
  </svg>
)

// The docs door is THE PRIMARY PATH — most suppliers can hand us documentation
// but cannot fill in a request-shape form (module header). It gets its own wide
// card; the other two are the smaller, secondary row, and `api` is flagged as
// the advanced (technical) option so nobody who shouldn't touch it feels they
// must.
const PRIMARY_CARD: MethodCard = {
  key: 'docs',
  title: 'Send us your API docs',
  blurb: 'Upload the PDF or paste a link. Our team reads it and builds the connection — you don’t need to know the technical details.',
  badge: 'Recommended',
  icon: ICON_DOCS,
}

const SECONDARY_CARDS: MethodCard[] = [
  {
    key: 'api',
    title: 'I have the endpoint details',
    blurb: 'Paste the URL, tell us how to call it, and test it right here.',
    badge: 'Advanced',
    icon: ICON_API,
  },
  {
    key: 'excel',
    title: 'Excel / CSV sheet',
    blurb: 'Upload a sample of your stock sheet — we check the columns.',
    icon: ICON_EXCEL,
  },
]

const SHEET_ACCEPT = '.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv'
const DOCS_ACCEPT = '.pdf,.doc,.docx,.txt,.md,.json,.yaml,.yml,.xlsx,.csv,.png,.jpg,.jpeg'
const MAX_BYTES = 10 * 1024 * 1024

export function DataSourceSetup({ draft, onChange, canEdit = true }: {
  draft: DataSourceDraft
  onChange: (d: DataSourceDraft) => void
  /** False for a plain org member — the form is read-only rather than a 403. */
  canEdit?: boolean
}) {
  const patch = (p: Partial<DataSourceDraft>) => onChange({ ...draft, ...p })
  // Changing the request shape (URL / method / body / auth) invalidates a prior
  // green test — otherwise draftIsSubmittable would accept a stale `lastTest.ok`
  // over a connection the supplier has since edited into something untested.
  const patchConn = (p: Partial<DataSourceDraft>) => onChange({ ...draft, ...p, lastTest: null })
  const reduce = useReducedMotion()
  const [testing, setTesting] = useState(false)
  const [uploading, setUploading] = useState<null | 'sheet' | 'docs'>(null)
  const [verifying, setVerifying] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  async function runTest() {
    if (!draft.apiUrl.trim()) { setActionError('Add the feed URL first.'); return }
    setTesting(true)
    setActionError(null)
    try {
      const { result } = await supplierDataApi.testApi({
        connection: draftToConnection(draft),
        ...(draft.apiKey.trim() ? { apiKey: draft.apiKey.trim() } : {}),
        answers: draft.answers,
      })
      patch({ lastTest: result })
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Test failed — check your connection and try again.')
    } finally {
      setTesting(false)
    }
  }

  async function pickSheet(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_BYTES) { setActionError('That file is over 10 MB — a sample of a few hundred rows is plenty.'); return }
    setUploading('sheet')
    setActionError(null)
    try {
      const uploaded = await supplierDataApi.uploadFile('sample_sheet', file)
      patch({ sheet: uploaded, sheetVerification: null })
      setUploading(null)
      setVerifying(true)
      const { result } = await supplierDataApi.verifySheet(uploaded)
      onChange({ ...draft, sheet: uploaded, sheetVerification: result })
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Upload failed — try again.')
    } finally {
      setUploading(null)
      setVerifying(false)
    }
  }

  async function pickDocs(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_BYTES) { setActionError('That file is over 10 MB.'); return }
    setUploading('docs')
    setActionError(null)
    try {
      const uploaded = await supplierDataApi.uploadFile('api_docs', file)
      patch({ docsFile: uploaded })
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Upload failed — try again.')
    } finally {
      setUploading(null)
    }
  }

  const proposal = proposalOf(draft)

  const pickMethod = (key: SupplierDataMethod) => {
    setActionError(null)
    patch({ method: draft.method === key ? null : key })
  }

  return (
    <fieldset disabled={!canEdit} className="min-w-0 disabled:opacity-70" data-testid="datasource-setup">
      <div className="space-y-2.5" role="radiogroup" aria-label="How does your stock reach us">
        {/* The primary door, given its own full-width card. */}
        <MethodButton
          card={PRIMARY_CARD}
          active={draft.method === PRIMARY_CARD.key}
          primary
          reduce={!!reduce}
          index={0}
          onPick={() => pickMethod(PRIMARY_CARD.key)}
        />
        {/* The two secondary doors. `api` carries the "Advanced" badge. */}
        <div className="grid gap-2.5 sm:grid-cols-2">
          {SECONDARY_CARDS.map((m, i) => (
            <MethodButton
              key={m.key}
              card={m}
              active={draft.method === m.key}
              reduce={!!reduce}
              index={i + 1}
              onPick={() => pickMethod(m.key)}
            />
          ))}
        </div>
      </div>

      {draft.method === 'docs' && (
        <div className="mt-5 space-y-3 rounded-md border border-line bg-paper p-4" data-testid="datasource-docs-pane">
          <p className="text-[13px] leading-relaxed text-navy-500">
            Whatever your integration team already wrote — an API guide, a sample response, even a screenshot of
            the fields. If your ERP pushes stock out rather than serving it, send those docs and say so below.
          </p>
          <label
            className={`flex items-center gap-4 rounded-md border p-4 cursor-pointer transition-all min-h-[44px] ${
              draft.docsFile ? 'border-success/50 bg-success/5' : 'border-dashed border-line-strong hover:border-navy-300'
            }`}
            data-testid="datasource-docs-upload"
          >
            <input type="file" accept={DOCS_ACCEPT} className="sr-only" onChange={(e) => void pickDocs(e)} />
            <span className={`shrink-0 grid place-items-center h-10 w-10 rounded-full border ${draft.docsFile ? 'border-success/40 text-success' : 'border-line text-navy-300'}`} aria-hidden>
              {draft.docsFile ? '✓' : '↑'}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink">
                {uploading === 'docs' ? 'Uploading…' : draft.docsFile ? draft.docsFile.fileName : 'Upload your API documentation'}
              </span>
              <span className="block text-xs text-navy-500">
                {draft.docsFile ? 'Tap to replace' : 'PDF, Word, text or a sheet. Up to 10 MB.'}
              </span>
            </span>
          </label>
          <input
            type="text"
            inputMode="url"
            value={draft.docsUrl}
            onChange={(e) => patch({ docsUrl: e.target.value })}
            placeholder="…or paste a link to your docs — https://docs.yourcompany.com/stock"
            className="input h-11 text-base w-full"
            data-testid="datasource-docs-url"
          />
          <details className="rounded-md border border-line-strong/60 px-3 py-2">
            <summary className="cursor-pointer text-[13px] font-semibold text-ink min-h-[32px] flex items-center">
              Know your endpoint URL? (optional)
            </summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                inputMode="url"
                value={draft.apiUrl}
                onChange={(e) => patch({ apiUrl: e.target.value })}
                placeholder="https://api.yourcompany.com/stock"
                className="input h-11 text-base"
                data-testid="datasource-docs-endpoint"
              />
              <input
                type="password"
                autoComplete="off"
                value={draft.apiKey}
                onChange={(e) => patch({ apiKey: e.target.value })}
                placeholder={draft.hasSavedKey ? 'API key (saved — retype to change)' : 'API key, if it needs one'}
                className="input h-11 text-base"
              />
            </div>
          </details>
          <NotesBox draft={draft} patch={patch} />
        </div>
      )}

      {draft.method === 'api' && (
        <div className="mt-5 space-y-3 rounded-md border border-line bg-paper p-4" data-testid="datasource-api-pane">
          {/* This door is for someone who can read a request shape. Most can't —
              they get the docs door — so orient the rest with a cURL example. */}
          <details className="rounded-md border border-line-strong/60 bg-cream/40 px-3 py-2" data-testid="datasource-api-curl-hint">
            <summary className="cursor-pointer text-[13px] font-semibold text-ink min-h-[32px] flex items-center">
              Have a cURL command from your vendor? Read it like this.
            </summary>
            <div className="mt-2 space-y-1.5 text-[12.5px] leading-relaxed text-navy-500">
              <p className="font-mono text-[11.5px] text-ink break-all">
                curl -X POST -H 'X-API-Key: abc' -d '{'{'}…{'}'}' https://api.you.com/stock
              </p>
              <p>
                becomes → <span className="font-medium text-ink">Method</span> = POST ·{' '}
                <span className="font-medium text-ink">Body</span> = JSON (paste what follows <span className="font-mono">-d</span>) ·{' '}
                <span className="font-medium text-ink">Auth</span> = A named header, name <span className="font-mono">X-API-Key</span>, value <span className="font-mono">abc</span>.
              </p>
              <p>
                <span className="font-mono">-H 'Authorization: Bearer …'</span> → Auth = Bearer.{' '}
                <span className="font-mono">?key=abc</span> in the URL → Auth = a query parameter named <span className="font-mono">key</span>.
              </p>
            </div>
          </details>

          <input
            type="text"
            inputMode="url"
            value={draft.apiUrl}
            onChange={(e) => patchConn({ apiUrl: e.target.value })}
            placeholder="Feed URL — https://api.yourcompany.com/stock"
            className="input h-11 text-base w-full"
            data-testid="datasource-api-url"
          />

          {/* Method + body. Every feed we ingest is a POST — a GET-only form was
              why none of our real suppliers could pass this step. */}
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              label="Request"
              value={draft.httpMethod}
              options={[{ value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' }]}
              onChange={(v) => patchConn({ httpMethod: v as 'GET' | 'POST', bodyMode: v === 'POST' ? (draft.bodyMode === 'none' ? 'json' : draft.bodyMode) : 'none' })}
              testId="datasource-api-method"
            />
            {draft.httpMethod === 'POST' && (
              <Segmented
                label="Body"
                value={draft.bodyMode}
                options={[
                  { value: 'json', label: 'JSON' },
                  { value: 'form', label: 'Form fields' },
                  { value: 'none', label: 'None' },
                ]}
                onChange={(v) => patchConn({ bodyMode: v as DataSourceDraft['bodyMode'] })}
                testId="datasource-api-bodymode"
              />
            )}
          </div>

          {draft.httpMethod === 'POST' && draft.bodyMode === 'json' && (
            <textarea
              value={draft.bodyJson}
              onChange={(e) => patchConn({ bodyJson: e.target.value })}
              rows={3}
              spellCheck={false}
              placeholder='{ }'
              className="input w-full font-mono text-[13px] leading-relaxed"
              data-testid="datasource-api-body"
            />
          )}

          {draft.httpMethod === 'POST' && draft.bodyMode === 'form' && (
            <KeyValueRows
              rows={draft.formFields}
              onChange={(formFields) => patchConn({ formFields })}
              namePlaceholder="Field name — e.g. diamondtype"
              valuePlaceholder="Value — e.g. ALL"
              testId="datasource-api-form"
            />
          )}

          {/* Where the credential goes. Three real suppliers, three answers. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-[13px] text-navy-500">
              <span className="mb-1 block font-medium text-ink">How is your key sent?</span>
              <select
                value={draft.authMode}
                onChange={(e) => patchConn({ authMode: e.target.value as DataSourceDraft['authMode'] })}
                className="input h-11 w-full text-base"
                data-testid="datasource-api-authmode"
              >
                <option value="none">No key needed</option>
                <option value="bearer">Authorization: Bearer</option>
                <option value="header">A named header</option>
                <option value="query">A query parameter</option>
              </select>
            </label>
            {draft.authMode !== 'none' && (
              <label className="text-[13px] text-navy-500">
                <span className="mb-1 block font-medium text-ink">API key</span>
                <input
                  type="password"
                  autoComplete="off"
                  value={draft.apiKey}
                  onChange={(e) => patchConn({ apiKey: e.target.value })}
                  placeholder={draft.hasSavedKey ? 'Saved — retype to change' : 'Paste your key'}
                  className="input h-11 w-full text-base"
                  data-testid="datasource-api-key"
                />
              </label>
            )}
            {draft.authMode === 'header' && (
              <input
                type="text"
                autoComplete="off"
                value={draft.apiKeyHeader}
                onChange={(e) => patchConn({ apiKeyHeader: e.target.value })}
                placeholder="Header name — e.g. apikey, X-API-Key"
                className="input h-11 text-base"
                data-testid="datasource-api-header"
              />
            )}
            {draft.authMode === 'query' && (
              <input
                type="text"
                autoComplete="off"
                value={draft.apiKeyParam}
                onChange={(e) => patchConn({ apiKeyParam: e.target.value })}
                placeholder="Parameter name — e.g. APIToken"
                className="input h-11 text-base"
                data-testid="datasource-api-param"
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void runTest()}
              disabled={testing}
              className="btn-secondary h-11 px-5 text-sm font-semibold whitespace-nowrap disabled:opacity-60"
              data-testid="datasource-api-test"
            >
              {testing ? 'Reading your feed…' : 'Test connection'}
            </button>
            <span className="text-xs text-navy-300">We call it once, right now, and show you what we could read.</span>
          </div>
          {!draft.lastTest?.ok && (
            <p className="text-xs text-navy-500" data-testid="datasource-api-test-required">
              A green test is required to finish this option — or choose “Send us your API docs” above, or “I’ll do this later”.
            </p>
          )}

          {draft.lastTest && (
            <div
              className={`rounded-md border p-3 text-[13px] leading-relaxed ${
                draft.lastTest.ok ? 'border-success/40 bg-success/5 text-ink' : 'border-warning/50 bg-warning/10 text-ink'
              }`}
              data-testid="datasource-api-result"
            >
              <span className="font-semibold">{draft.lastTest.ok ? '✓ ' : '✕ '}</span>
              {draft.lastTest.note}
              {draft.lastTest.request && (
                <span className="mt-1 block font-mono text-[11px] text-navy-300">
                  We sent: {draft.lastTest.request}
                </span>
              )}
              {!draft.lastTest.ok && (
                <button
                  type="button"
                  onClick={() => patch({ method: 'docs' })}
                  className="mt-2 block text-[13px] font-semibold text-gold-600 underline underline-offset-4 min-h-[32px]"
                  data-testid="datasource-api-fallback"
                >
                  Not sure what to fill in? Send us your API docs instead →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {draft.method === 'excel' && (
        <div className="mt-5 space-y-3 rounded-md border border-line bg-paper p-4" data-testid="datasource-excel-pane">
          <label
            className={`flex items-center gap-4 rounded-md border p-4 cursor-pointer transition-all min-h-[44px] ${
              draft.sheet ? 'border-success/50 bg-success/5' : 'border-dashed border-line-strong hover:border-navy-300'
            }`}
            data-testid="datasource-sheet-upload"
          >
            <input type="file" accept={SHEET_ACCEPT} className="sr-only" onChange={(e) => void pickSheet(e)} />
            <span className={`shrink-0 grid place-items-center h-10 w-10 rounded-full border ${draft.sheet ? 'border-success/40 text-success' : 'border-line text-navy-300'}`} aria-hidden>
              {draft.sheet ? '✓' : '↑'}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink">
                {uploading === 'sheet' ? 'Uploading…' : verifying ? 'Reading your columns…' : draft.sheet ? draft.sheet.fileName : 'Upload a sample of your stock sheet'}
              </span>
              <span className="block text-xs text-navy-500">
                {draft.sheet ? 'Tap to replace' : 'XLSX or CSV, a few hundred rows is plenty. Up to 10 MB.'}
              </span>
            </span>
          </label>
          {draft.sheetVerification && (
            <div
              className={`rounded-md border p-3 text-[13px] leading-relaxed ${
                draft.sheetVerification.ok ? 'border-success/40 bg-success/5' : 'border-warning/50 bg-warning/10'
              }`}
              data-testid="datasource-sheet-result"
            >
              {draft.sheetVerification.ok ? (
                <p className="text-ink">
                  <span className="font-semibold">✓ Your sheet has everything we need</span>
                  {typeof draft.sheetVerification.rowCount === 'number' ? ` — ${draft.sheetVerification.rowCount.toLocaleString()} rows.` : '.'}
                </p>
              ) : (
                <p className="text-ink">
                  <span className="font-semibold">We couldn’t find:</span>{' '}
                  {draft.sheetVerification.missingRequired.join(', ')}. Tell us which columns those are below, or
                  add them and re-upload.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* The review. Same table whichever door produced it. */}
      {draft.method !== null && proposal && (
        <MappingReview
          draft={draft}
          proposal={proposal}
          onChange={onChange}
          onError={setActionError}
        />
      )}

      {actionError && (
        <p className="mt-3 text-sm text-danger" role="alert" data-testid="datasource-error">{actionError}</p>
      )}
    </fieldset>
  )
}

// ─── The mapping review ─────────────────────────────────────────────────────

/**
 * "Here is what we understood. Is it right, and what are these?"
 *
 * A silent mis-map is the expensive failure on an exchange: read a discount
 * column as a per-carat rate and every stone from that supplier is priced
 * wrong, to real buyers, with no error anywhere. So this shows the supplier
 * THEIR OWN VALUES beside our field names, asks about what we could not place,
 * and requires an explicit tick before anything is treated as confirmed.
 */
function MappingReview({ draft, proposal, onChange, onError }: {
  draft: DataSourceDraft
  proposal: MappingProposal
  onChange: (d: DataSourceDraft) => void
  onError: (m: string | null) => void
}) {
  const patch = (p: Partial<DataSourceDraft>) => onChange({ ...draft, ...p })
  const [checkingMedia, setCheckingMedia] = useState(false)

  const mediaSamples = [...proposal.media.imageSamples, ...proposal.media.videoSamples].slice(0, 3)

  async function checkMedia() {
    setCheckingMedia(true)
    onError(null)
    try {
      const { result } = await supplierDataApi.mediaCheck({
        ...(mediaSamples.length ? { urls: mediaSamples } : {}),
        ...(draft.mediaPattern.trim() ? { pattern: draft.mediaPattern.trim() } : {}),
        ...(sampleStockId(proposal) ? { sampleStockId: sampleStockId(proposal)! } : {}),
      })
      patch({ mediaCheck: result })
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Couldn’t check those links — try again.')
    } finally {
      setCheckingMedia(false)
    }
  }

  const placed = proposal.fields.filter((f) => f.source)

  return (
    <div className="mt-5 space-y-4 rounded-md border border-line bg-paper p-4" data-testid="datasource-mapping">
      <div>
        <h3 className="text-sm font-semibold text-ink">What we understood</h3>
        <p className="mt-1 text-xs text-navy-500">
          {proposal.recordCount.toLocaleString()} stones read. Check the values on the right are what you expect —
          if a column is on the wrong row, tell us below.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] text-left text-[13px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-navy-300">
              <th className="pb-1.5 pr-3 font-semibold">Our field</th>
              <th className="pb-1.5 pr-3 font-semibold">Your column</th>
              <th className="pb-1.5 font-semibold">Your values</th>
            </tr>
          </thead>
          <tbody className="align-top">
            {placed.map((f) => (
              <tr key={f.canonical} className="border-t border-line/70" data-testid={`datasource-field-${f.canonical}`}>
                <td className="py-1.5 pr-3 text-navy-500">{f.label}</td>
                <td className="py-1.5 pr-3 font-medium text-ink">{f.source}</td>
                <td className="py-1.5 font-mono text-[12px] text-navy-500">{f.samples.join(' · ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {proposal.missingRequired.length > 0 && (
        <p className="rounded-md border border-warning/50 bg-warning/10 p-3 text-[13px] text-ink" data-testid="datasource-missing">
          <span className="font-semibold">We still can’t find:</span> {proposal.missingRequired.join(', ')}.
          Answer the questions below, or send us your docs and we’ll work it out with you.
        </p>
      )}

      {proposal.questions.length > 0 && (
        <div className="space-y-2.5" data-testid="datasource-questions">
          <h3 className="text-sm font-semibold text-ink">A few things we’re not sure about</h3>
          {proposal.questions.map((q) => {
            // The AI's suggestion for THIS column, if it made one. It is only a
            // hint — the supplier must pick it (or anything else) themselves; a
            // suggestion is never a saved answer until they act on it.
            const ai = (proposal.aiSuggestions ?? []).find((s) => s.sourceKey === q.sourceKey)
            const aiLabel = ai
              ? (q.options.find((o) => o.value === ai.canonical)?.label ?? ai.canonical)
              : null
            const unanswered = !(draft.answers[q.sourceKey] ?? '')
            return (
            <div key={q.id} className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
              <div className="min-w-0">
                <p className="text-[13px] text-ink">{q.prompt}</p>
                <p className="truncate font-mono text-[11px] text-navy-300">{q.samples.join(' · ') || 'no sample values'}</p>
                {ai && (
                  <p className="mt-0.5 text-[11px] text-navy-500" data-testid={`datasource-ai-hint-${q.sourceKey}`}>
                    <span className="font-medium text-ink">AI:</span> {ai.question}
                    {unanswered && aiLabel && (
                      <button
                        type="button"
                        className="ml-1 underline decoration-dotted underline-offset-2 hover:text-gold"
                        onClick={() => patch({ answers: { ...draft.answers, [q.sourceKey]: ai.canonical }, mappingConfirmed: false })}
                        data-testid={`datasource-ai-apply-${q.sourceKey}`}
                      >Use “{aiLabel}”</button>
                    )}
                  </p>
                )}
              </div>
              <select
                value={draft.answers[q.sourceKey] ?? ''}
                onChange={(e) => {
                  const answers = { ...draft.answers }
                  if (e.target.value) answers[q.sourceKey] = e.target.value
                  else delete answers[q.sourceKey]
                  // Any answer invalidates the tick — they must re-read the table.
                  patch({ answers, mappingConfirmed: false })
                }}
                className="input h-10 text-sm sm:w-56"
                data-testid={`datasource-answer-${q.sourceKey}`}
                aria-label={q.prompt}
              >
                <option value="">Choose…</option>
                {q.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            )
          })}
          <p className="text-xs text-navy-300">
            Changed an answer? Run <span className="font-medium">Test connection</span> again to see the table update.
          </p>
        </div>
      )}

      {/* Media. The half of a diamond feed that is never in the stock API. */}
      <div className="space-y-2.5 border-t border-line/70 pt-4" data-testid="datasource-media">
        <h3 className="text-sm font-semibold text-ink">Photos and videos</h3>
        {proposal.media.hasMedia ? (
          <p className="text-xs text-navy-500">
            We found media links in your data. Let’s make sure they actually load from our servers.
          </p>
        ) : (
          <p className="text-xs text-navy-500">
            Your feed has no image or video links. If your photos live somewhere else, give us the pattern —
            put <span className="font-mono">{'{stockId}'}</span> where the stock number goes.
          </p>
        )}
        <input
          type="text"
          inputMode="url"
          value={draft.mediaPattern}
          onChange={(e) => patch({ mediaPattern: e.target.value })}
          placeholder="https://cdn.yourcompany.com/{stockId}.jpg"
          className="input h-11 w-full text-base"
          data-testid="datasource-media-pattern"
        />
        <button
          type="button"
          onClick={() => void checkMedia()}
          disabled={checkingMedia || (mediaSamples.length === 0 && !draft.mediaPattern.trim())}
          className="btn-secondary h-10 px-4 text-sm font-semibold disabled:opacity-50"
          data-testid="datasource-media-check"
        >
          {checkingMedia ? 'Checking…' : 'Check my images'}
        </button>
        {draft.mediaCheck && (
          <ul className="space-y-1.5 text-[13px]" data-testid="datasource-media-result">
            {draft.mediaCheck.results.map((r) => (
              <li key={r.url} className={r.ok ? 'text-ink' : 'text-warning'}>
                <span className="font-semibold">{r.ok ? '✓ ' : '✕ '}</span>
                <span className="break-all font-mono text-[11px]">{r.url}</span>
                <span className="block text-xs text-navy-500">{r.note}</span>
              </li>
            ))}
            {draft.mediaCheck.results.some((r) => !r.ok) && (
              <li className="text-xs text-navy-500">
                Do you have a different link for these — or a URL pattern we can build from the stock number?
              </li>
            )}
          </ul>
        )}
      </div>

      <NotesBox draft={draft} patch={patch} />

      <label className="flex items-start gap-2.5 text-[13px] text-ink">
        <input
          type="checkbox"
          checked={draft.mappingConfirmed}
          onChange={(e) => patch({ mappingConfirmed: e.target.checked })}
          className="mt-0.5 h-4 w-4 shrink-0"
          data-testid="datasource-confirm"
        />
        <span>
          I’ve checked this table and the columns are read correctly.
          <span className="block text-xs text-navy-300">
            We won’t publish prices from a mapping nobody confirmed.
          </span>
        </span>
      </label>
    </div>
  )
}

/** The first stock number in the sample — what a media pattern is tested with. */
function sampleStockId(proposal: MappingProposal): string | null {
  return proposal.fields.find((f) => f.canonical === 'stockId')?.samples[0] ?? null
}

// ─── Small shared controls ──────────────────────────────────────────────────

function NotesBox({ draft, patch }: { draft: DataSourceDraft; patch: (p: Partial<DataSourceDraft>) => void }) {
  return (
    <label className="block text-[13px]">
      <span className="mb-1 block font-medium text-ink">Anything else we should know?</span>
      <textarea
        value={draft.notes}
        onChange={(e) => patch({ notes: e.target.value })}
        rows={3}
        maxLength={4000}
        placeholder="e.g. “Rate is our list price, Net Rate is what we actually sell at” — or how your ERP prefers to push stock."
        className="input w-full text-base"
        data-testid="datasource-notes"
      />
    </label>
  )
}

/** One method card — colored, frosted, iconed. `primary` is the RECOMMENDED
 *  door: it wears a gold rim + a corner ribbon and always reads a touch lifted.
 *  Icon tile tones to gold when the card is active/recommended, navy otherwise.
 *  Hover lift + press are framer springs (reduced-motion aware); entrance is a
 *  staggered fade-up keyed off `index`. Behavior (onPick / role / aria) is
 *  unchanged from the old button. */
function MethodButton({ card, active, primary = false, reduce, index, onPick }: {
  card: MethodCard
  active: boolean
  primary?: boolean
  reduce: boolean
  index: number
  onPick: () => void
}) {
  const advanced = card.badge === 'Advanced'
  // Icon-tile tone: gold for the recommended / selected path, navy for the rest.
  const goldTile = active || primary
  return (
    <motion.button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onPick}
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.42, delay: reduce ? 0 : 0.05 + index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      whileHover={reduce ? undefined : { y: -3 }}
      whileTap={reduce ? undefined : { scale: 0.985 }}
      className={`group relative w-full overflow-hidden text-left rounded-card border p-4 transition-shadow duration-default ease-premium min-h-[44px] ${
        active
          ? 'border-gold-500 shadow-elev-3'
          : primary
            ? 'border-gold-500/55 shadow-elev-1 hover:shadow-elev-3'
            : 'border-line shadow-elev-0 hover:shadow-elev-2'
      }`}
      style={
        active
          ? { background: 'linear-gradient(135deg, rgba(201,169,74,0.14), rgba(201,169,74,0.05))' }
          : primary
            ? { background: 'linear-gradient(135deg, rgba(201,169,74,0.09), rgba(255,255,255,0))' }
            : { background: 'var(--paper)' }
      }
      data-testid={`datasource-method-${card.key}`}
    >
      {/* corner ribbon — only on the recommended card */}
      {primary && card.badge && (
        <span
          className="pointer-events-none absolute -right-9 top-3 rotate-45 px-9 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-navy-900 shadow-sm"
          style={{ background: 'linear-gradient(90deg, #e4cd86, #c9a94a)' }}
          aria-hidden
        >
          {card.badge}
        </span>
      )}
      <span className={`flex items-start gap-3.5 ${primary ? 'pr-8' : ''}`}>
        {/* icon tile */}
        <span
          className={`grid shrink-0 place-items-center rounded-[10px] transition-colors ${primary ? 'h-11 w-11' : 'h-10 w-10'}`}
          style={
            goldTile
              ? { background: 'rgba(201,169,74,0.15)', color: 'var(--gold-600)', border: '1px solid rgba(201,169,74,0.4)' }
              : { background: 'rgba(35,69,111,0.07)', color: 'var(--navy-500)', border: '1px solid var(--line)' }
          }
          aria-hidden
        >
          {card.icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={`font-semibold text-ink ${primary ? 'text-[15px]' : 'text-sm'}`}>{card.title}</span>
            {/* the "Advanced" pill stays inline (secondary cards have no ribbon) */}
            {card.badge && !primary && !active && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                advanced ? 'bg-navy-050 text-navy-500' : 'bg-gold-050 text-gold-600'
              }`}>
                {card.badge}
              </span>
            )}
          </span>
          <span className="mt-1 block text-xs leading-relaxed text-navy-500">{card.blurb}</span>
        </span>
        {/* selected diamond tick */}
        <span
          aria-hidden
          className={`mt-1 h-3.5 w-3.5 shrink-0 rotate-45 rounded-[3px] border transition-all ${
            active ? 'border-gold-500 bg-gold-500' : 'border-line-strong bg-transparent'
          }`}
        />
      </span>
    </motion.button>
  )
}

function Segmented({ label, value, options, onChange, testId }: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
  testId: string
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-navy-500">{label}</span>
      <div className="inline-flex overflow-hidden rounded-md border border-line" role="group" aria-label={label} data-testid={testId}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
            className={`min-h-[36px] px-3 text-xs font-semibold transition-colors ${
              value === o.value ? 'bg-navy text-white' : 'bg-paper text-navy-500 hover:bg-line/30'
            }`}
            data-testid={`${testId}-${o.value.toLowerCase()}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function KeyValueRows({ rows, onChange, namePlaceholder, valuePlaceholder, testId }: {
  rows: Array<{ name: string; value: string }>
  onChange: (rows: Array<{ name: string; value: string }>) => void
  namePlaceholder: string
  valuePlaceholder: string
  testId: string
}) {
  const list = rows.length ? rows : [{ name: '', value: '' }]
  const set = (i: number, p: Partial<{ name: string; value: string }>) =>
    onChange(list.map((r, n) => (n === i ? { ...r, ...p } : r)))
  return (
    <div className="space-y-2" data-testid={testId}>
      {list.map((r, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-2">
          <input
            type="text"
            value={r.name}
            onChange={(e) => set(i, { name: e.target.value })}
            placeholder={namePlaceholder}
            className="input h-11 text-base"
            data-testid={`${testId}-name-${i}`}
          />
          <input
            type="text"
            value={r.value}
            onChange={(e) => set(i, { value: e.target.value })}
            placeholder={valuePlaceholder}
            className="input h-11 text-base"
            data-testid={`${testId}-value-${i}`}
          />
        </div>
      ))}
      {list.length < 6 && (
        <button
          type="button"
          onClick={() => onChange([...list, { name: '', value: '' }])}
          className="text-xs font-semibold text-navy-500 underline underline-offset-4 min-h-[32px]"
          data-testid={`${testId}-add`}
        >
          + Add another field
        </button>
      )}
    </div>
  )
}
